import React from 'react';
import { Image, Pressable } from 'react-native';

import { HorizontalScrollView, Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import {
  getReportAttachmentCapacity,
  type ReportAttachment,
} from '@/features/guardian-profile/schemas/guardian-report-issue-schema';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

const strings = STRINGS.guardian.reportIssue;

type ReportAttachmentFieldProps = {
  attachments: ReportAttachment[];
  onPick: () => void;
  onRemove: (uri: string) => void;
  /** Tapping a tile opens the full-screen preview at that index. */
  onPreview: (index: number) => void;
  /** Inline validation after a rejected pick (size or count limit). */
  errorMessage?: string | null;
  disabled?: boolean;
};

/**
 * Evidence field: full-width dashed CTA when empty; once media is attached, switches to a
 * thumbnail strip with a compact add tile. Validation copy only appears after a failed pick.
 */
export function ReportAttachmentField({
  attachments,
  onPick,
  onRemove,
  onPreview,
  errorMessage = null,
  disabled = false,
}: ReportAttachmentFieldProps) {
  const styles = useStyles();
  const { scale } = useScaling();
  const { canAddMore } = getReportAttachmentCapacity(attachments);
  const isEmpty = attachments.length === 0;

  return (
    <Stack gap={scale(2)}>
      <Stack gap={scale(2)}>
        <Text variant="Font Guardian App/Subtitle/Subtitle 12 Semibold" style={styles.label}>
          {isEmpty ? strings.uploadLabel : strings.uploadLabelWithLimit}
        </Text>

        {isEmpty ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={strings.uploadPlaceholder}
            accessibilityState={{ disabled }}
            disabled={disabled}
            onPress={onPick}
            style={({ pressed }) => [styles.emptyUpload, pressed ? styles.pressed : null]}>
            <SvgIcon name="IcUpload" size={scale(20)} color={guardianColors['6']} />
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
              style={styles.emptyUploadText}>
              {strings.uploadPlaceholder}
            </Text>
          </Pressable>
        ) : (
          <HorizontalScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={styles.previewRow}>
            {canAddMore ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={strings.uploadPlaceholder}
                accessibilityState={{ disabled }}
                disabled={disabled}
                onPress={onPick}
                style={({ pressed }) => [styles.uploadTile, pressed ? styles.pressed : null]}>
                <SvgIcon name="IcUpload" size={scale(24)} color={guardianColors['6']} />
              </Pressable>
            ) : null}

            {attachments.map((attachment, index) => (
              <Stack key={attachment.uri} style={styles.thumb}>
                <Pressable
                  accessibilityRole="imagebutton"
                  accessibilityLabel={strings.previewAttachment}
                  onPress={() => onPreview(index)}
                  style={({ pressed }) => [styles.thumbPressable, pressed ? styles.pressed : null]}>
                  {attachment.kind === 'image' ? (
                    <Image source={{ uri: attachment.uri }} style={styles.thumbImage} />
                  ) : (
                    <Stack align="center" justify="center" style={styles.videoTile}>
                      <SvgIcon name="IcCamera" size={scale(20)} color={colors['0']} />
                      <Text
                        variant="Font Guardian App/Subtitle/Subtitle 10 Regular"
                        style={styles.videoLabel}>
                        {strings.videoBadge}
                      </Text>
                    </Stack>
                  )}
                </Pressable>

                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={strings.removeAttachment}
                  disabled={disabled}
                  hitSlop={8}
                  onPress={() => onRemove(attachment.uri)}
                  style={styles.removeButton}>
                  <SvgIcon
                    name="IcXClose"
                    size={scale(12)}
                    color={guardianColors['6']}
                    strokeWidth={1.5}
                  />
                </Pressable>
              </Stack>
            ))}
          </HorizontalScrollView>
        )}
      </Stack>

      {errorMessage ? (
        <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.error}>
          {errorMessage}
        </Text>
      ) : null}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  label: { color: guardianColors['10'] },
  emptyUpload: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: scale(6),
    height: scale(48),
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: guardianColors['6'],
    borderRadius: scale(8),
    borderCurve: 'continuous',
    backgroundColor: colors['0'],
  },
  emptyUploadText: { color: guardianColors['6'] },
  previewRow: {
    gap: scale(8),
    paddingRight: scale(4),
  },
  uploadTile: {
    width: scale(64),
    height: scale(64),
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: guardianColors['6'],
    borderRadius: scale(8),
    borderCurve: 'continuous',
    backgroundColor: colors['0'],
  },
  pressed: { opacity: 0.68 },
  thumb: {
    width: scale(64),
    height: scale(64),
    borderRadius: scale(8),
    borderCurve: 'continuous',
    overflow: 'visible',
  },
  thumbPressable: { width: '100%', height: '100%' },
  thumbImage: {
    width: '100%',
    height: '100%',
    borderRadius: scale(8),
    backgroundColor: guardianColors['2'],
  },
  videoTile: {
    width: '100%',
    height: '100%',
    gap: scale(2),
    borderRadius: scale(8),
    backgroundColor: guardianColors['10'],
  },
  videoLabel: { color: colors['0'] },
  removeButton: {
    position: 'absolute',
    top: scale(6),
    right: scale(6),
    width: scale(16),
    height: scale(16),
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: scale(9),
    backgroundColor: colors['0'],
  },
  error: { color: guardianColors.Error },
}));

export default ReportAttachmentField;
